import { useEffect, useState } from 'react';
import {
  CONSUMER_CMS_SHOPPING_CART,
  getBestTechFromCoverage,
  IGlobalConfigCMS,
  isHiddenVfFwFrontend,
  organizeBestTechFromServiceAbility,
  retrieveProductTechnology,
  useCmsConfig,
  useCreateAddress,
  usePage,
  useServiceabilityAddressMutation,
  useValidateAddress,
} from '@vfit/consumer/data-access';
import { useQueryClient } from 'react-query';
import { CoverageErrorType, IBestTech } from '@vfit/shared/models';
import { addressInputIsValid, isLoggedR2 } from '../useCoverageTools.utils';
import { IUseCoverageToolProps, IUseCoverageToolResponse } from '../useCoverageTools.models';
import { API } from '@vfit/shared/data-access';

export const useCoverageToolVfFw = ({
  product,
  offerId,
  address,
  isAddressConfirmed,
}: IUseCoverageToolProps): IUseCoverageToolResponse => {
  const queryClient = useQueryClient();
  const cmsGlobalConfig = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GLOBAL_CONFIG
  ) as IGlobalConfigCMS;
  const { products: allProducts } = usePage();
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<number>(CoverageErrorType.KO);
  const [bestTech, setBestTech] = useState<IBestTech>({});
  const isHiddenFE = isHiddenVfFwFrontend(product, cmsGlobalConfig);

  const {
    data: validateAddressData,
    isSuccess: isSuccessValidateAddress,
    isError: isErrorValidateAddress,
    isLoading: isLoadingValidateAddress,
    mutate: mutateValidateAddress,
  } = useValidateAddress();
  const {
    data: serviceAbilityData,
    isSuccess: isSuccessServiceability,
    isError: isErrorServiceAbility,
    isLoading: isLoadingServiceAbility,
    mutate: mutateServiceability,
  } = useServiceabilityAddressMutation(
    'AMDOCS_FW',
    offerId,
    allProducts || [],
    product?.alternativeProductOfferList,
    product?.alternativeProductOfferListByTech,
    address?.placeId
  );
  const {
    isSuccess: isSuccessCreateAddress,
    isError: isErrorCreateAddress,
    isLoading: isLoadingCreateAddress,
    mutate: mutateCreateAddress,
  } = useCreateAddress();

  useEffect(() => {
    if (isAddressConfirmed) {
      if (isLoadingCreateAddress) {
        setIsLoading(true);
      } else if (isErrorCreateAddress) {
        setIsLoading(false);
        setIsError(true);
      } else if (isSuccessCreateAddress) {
        setIsSuccess(true);
        setIsLoading(false);
        setIsError(false);
      }
    }
  }, [isLoadingCreateAddress, isErrorCreateAddress, isSuccessCreateAddress]);

  useEffect(() => {
    if (isAddressConfirmed) {
      if (isLoadingServiceAbility) {
        setIsLoading(true);
      } else if (isErrorServiceAbility) {
        setIsLoading(false);
        setIsError(true);
      } else if (isSuccessServiceability) {
        const bestTech = organizeBestTechFromServiceAbility(serviceAbilityData);
        setBestTech(bestTech);
        if (serviceAbilityData?.serviceabilityResult?.toLowerCase() !== 'serviceable') {
          setIsLoading(false);
          setIsError(true);
          if (isHiddenFE) {
            setErrorType(CoverageErrorType.NO_COVERAGE);
          } else {
            const showOnlyFW = bestTech?.BestTechTechnology && bestTech?.bestTechValue;
            if (!showOnlyFW) {
              setErrorType(CoverageErrorType.NO_COVERAGE_AMD_FW);
            } else {
              setErrorType(CoverageErrorType.NO_COVERAGE_VODAFONE_ONLY_FW);
            }
          }
        } else {
          if (validateAddressData?.validAddressList?.[0]) {
            const bestTechFromCoverage = getBestTechFromCoverage(queryClient);
            if (bestTechFromCoverage?.fastwebTechnology?.mapping?.displayTechnology) {
              const productTechnology = retrieveProductTechnology();
              localStorage.setItem(
                'techOfferFw',
                `${productTechnology}_${bestTechFromCoverage?.fastwebTechnology?.mapping?.displayTechnology}`
              );
            }
            mutateCreateAddress({
              address: validateAddressData?.validAddressList?.[0],
            });
          } else {
            setIsLoading(false);
            setIsError(true);
            setErrorType(CoverageErrorType.KO);
          }
        }
      }
    }
  }, [isLoadingServiceAbility, isErrorServiceAbility, isSuccessServiceability]);

  const checkValidateAddress = () => {
    const showError = (errorType: CoverageErrorType) => {
      setIsLoading(false);
      setIsError(true);
      setErrorType(errorType);
    };
    const validAddressElement = validateAddressData?.validAddressList?.[0];
    if (validAddressElement) {
      mutateServiceability(validateAddressData?.validAddressList?.[0]);
    } else {
      showError(CoverageErrorType.KO);
    }
  };

  useEffect(() => {
    if (isAddressConfirmed) {
      if (isLoadingValidateAddress) {
        setIsLoading(true);
      } else if (isErrorValidateAddress) {
        setIsError(true);
      } else if (isSuccessValidateAddress) {
        checkValidateAddress();
      }
    }
  }, [isLoadingValidateAddress, isErrorValidateAddress, isSuccessValidateAddress]);

  useEffect(() => {
    if (addressInputIsValid(address) && isAddressConfirmed) {
      setIsLoading(true);
      setIsSuccess(false);
      setIsError(false);
      setErrorType(undefined);
      setBestTech(undefined);
      mutateValidateAddress({
        placeObject: address,
      });
    }
  }, [address, isAddressConfirmed]);

  useEffect(() => {
    if (isLoggedR2(queryClient)) {
      setErrorType(CoverageErrorType.R2_LOGGED);
      setIsLoading(false);
      setIsError(true);
    }
  }, []);

  return {
    isSuccess,
    isLoading,
    isError,
    errorType,
    bestTech,
  };
};
